"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface EdgeFadeScrollerProps {
  children: React.ReactNode;
  /** 스크롤러(flex 컨테이너)에 덧붙일 클래스 — gap/padding 등. */
  className?: string;
  /**
   * 바깥 래퍼(오버레이 기준 박스)에 덧붙일 클래스. 음수 마진(-mx-*)으로 부모 패딩을
   * 상쇄할 때는 스크롤러가 아니라 여기에 줘야 블러가 실제 스크롤 가장자리에 붙는다.
   */
  wrapperClassName?: string;
  /**
   * 가장자리 표현.
   * - `"white"`(기본): 흰 그라데이션 + 블러 오버레이 — 흰 바탕 위 줄.
   * - `"glass"`: 오버레이를 칠하지 않고 **스크롤러 내용만** 가장자리에서 투명하게
   *   지운다(mask). 지도처럼 바탕이 그림인 곳에서 흰 띠가 생기지 않게 쓴다.
   * - `"none"`: 페이드 없음(스크롤만).
   */
  fade?: "white" | "glass" | "none";
}

/** glass 마스크 폭 — 전폭 줄에서 거터(12~16px)를 넘어 알약이 녹아 보일 만큼 */
const GLASS_FADE_PX = 32;

/** 가려진 쪽만 지우는 마스크 — 맨 앞·맨 끝에서는 그쪽을 지우지 않는다. */
function glassMaskStyle(
  left: boolean,
  right: boolean,
): React.CSSProperties | undefined {
  if (!left && !right) return undefined;
  const from = left ? `transparent, #000 ${GLASS_FADE_PX}px` : "#000";
  const to = right ? `#000 calc(100% - ${GLASS_FADE_PX}px), transparent` : "#000";
  const mask = `linear-gradient(to right, ${from}, ${to})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

/**
 * 가로 스크롤러 + 양끝 블러 페이드.
 *
 * 더 스크롤할 방향에만 오버레이를 켜서 "옆에 더 있다"를 알린다. 화살표 버튼이
 * 없어도 되도록 오버레이는 `pointer-events-none` 이고, 스크롤 위치 계산은
 * `onScroll` 과 `ResizeObserver` 콜백에서만 한다(effect 본문 setState 금지 규칙).
 *
 * 전폭(화면 끝에서 끝) 줄: 래퍼에 음수 마진(-mx-N)으로 부모 거터를 상쇄하고, 거터는
 * 자식 쪽 `flex w-max px-N` 안쪽 줄에 준다. 스크롤러 자체 padding-right 는 일부
 * WebKit 에서 스크롤 끝 여백에 포함되지 않아 마지막 항목이 화면 끝에 붙는다 —
 * w-max 안쪽 줄은 패딩까지 자기 폭이라 끝 여백이 확실히 남는다.
 */
export function EdgeFadeScroller({
  children,
  className,
  wrapperClassName,
  fade = "white",
}: EdgeFadeScrollerProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // 스냅/패딩 때문에 정지 상태 scrollLeft 가 몇 px 남을 수 있어 여유를 둔다.
    const left = el.scrollLeft > 6;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 6;
    setEdges((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right },
    );
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // observe() 직후 콜백이 1회 오므로 초기 측정도 이 콜백이 처리한다.
    // 스크롤러 상자만 보면 안 된다(10-01): 알약이 늦게 그려지거나 웹폰트가 늦게 붙어
    // **내용 폭만** 늘면 상자 크기는 그대로라 콜백이 안 와서, 첫 로드에 오른쪽 페이드가
    // 꺼진 채 남았다. 자식(내용)도 함께 관찰하고, 자식이 바뀌면 다시 붙인다.
    const observer = new ResizeObserver(measure);
    const observeAll = () => {
      observer.disconnect();
      observer.observe(el);
      for (const child of Array.from(el.children)) observer.observe(child);
    };
    observeAll();
    const mutations =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(() => {
            observeAll();
            measure();
          });
    mutations?.observe(el, { childList: true });
    document.fonts?.ready.then(measure).catch(() => {});
    return () => {
      observer.disconnect();
      mutations?.disconnect();
    };
  }, [measure]);

  return (
    <div className={cn("relative", wrapperClassName)}>
      <div
        ref={scrollerRef}
        onScroll={measure}
        style={fade === "glass" ? glassMaskStyle(edges.left, edges.right) : undefined}
        className={cn("flex overflow-x-auto snap-x scrollbar-hide", className)}
      >
        {children}
      </div>
      {fade === "white" ? (
        <>
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-y-0 left-0 w-10 transition-opacity duration-200 backdrop-blur-[3px] bg-linear-to-r from-white/95 to-transparent [mask-image:linear-gradient(to_right,#000,transparent)]",
              edges.left ? "opacity-100" : "opacity-0",
            )}
          />
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-y-0 right-0 w-10 transition-opacity duration-200 backdrop-blur-[3px] bg-linear-to-l from-white/95 to-transparent [mask-image:linear-gradient(to_left,#000,transparent)]",
              edges.right ? "opacity-100" : "opacity-0",
            )}
          />
        </>
      ) : null}
    </div>
  );
}
